import React, { useEffect, useState } from 'react';
import api from '../../services/api';
import { useLanguage } from '../../contexts/LanguageContext';
import '../../styles/global.css';

/**
 * "Przewijarki" tab: rewinding machines offered to the WorkSpectrum rewinding station,
 * which records every finished rewinding against one of them.
 */
interface Rewinder {
  id: number;
  name: string;
  is_active: boolean;
  sort_order: number;
}

interface RewinderForm {
  name: string;
  sort_order: string;
  is_active: boolean;
}

const EMPTY_FORM: RewinderForm = { name: '', sort_order: '0', is_active: true };

const headerCell: React.CSSProperties = {
  padding: '16px 12px', fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.5px',
};

const errorMessage = (error: any, fallback: string): string => {
  const errors = error?.response?.data?.errors;
  const first = errors ? (Object.values(errors)[0] as string[] | undefined)?.[0] : undefined;
  return first || error?.response?.data?.message || fallback;
};

const Rewinders: React.FC = () => {
  const { t } = useLanguage();
  const [rewinders, setRewinders] = useState<Rewinder[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Rewinder | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState<RewinderForm>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const fetchRewinders = async () => {
    setLoading(true);
    try {
      const res = await api.get<{ data: Rewinder[] }>('/rewinders');
      setRewinders(res.data.data || []);
    } catch {
      alert(t('rewinders.fetchError'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRewinders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...EMPTY_FORM, sort_order: String(rewinders.length + 1) });
    setShowModal(true);
  };

  const openEdit = (rewinder: Rewinder) => {
    setEditing(rewinder);
    setForm({ name: rewinder.name, sort_order: String(rewinder.sort_order), is_active: rewinder.is_active });
    setShowModal(true);
  };

  const save = async (payload: Partial<Rewinder> & { name: string }, target: Rewinder | null) => {
    if (target) {
      await api.put(`/rewinders/${target.id}`, payload);
    } else {
      await api.post('/rewinders', payload);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await save({ name: form.name, sort_order: parseInt(form.sort_order, 10) || 0, is_active: form.is_active }, editing);
      setShowModal(false);
      fetchRewinders();
    } catch (error: any) {
      alert(errorMessage(error, t('rewinders.saveError')));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (rewinder: Rewinder) => {
    try {
      await save({ name: rewinder.name, is_active: !rewinder.is_active }, rewinder);
      fetchRewinders();
    } catch (error: any) {
      alert(errorMessage(error, t('rewinders.saveError')));
    }
  };

  const handleDelete = async (rewinder: Rewinder) => {
    if (!window.confirm(t('rewinders.confirmDelete', { name: rewinder.name }))) return;
    try {
      await api.delete(`/rewinders/${rewinder.id}`);
      fetchRewinders();
    } catch (error: any) {
      alert(errorMessage(error, t('rewinders.deleteError')));
    }
  };

  const smallButton = (color: string, filled: boolean): React.CSSProperties => ({
    padding: '6px 12px', fontSize: 13,
    backgroundColor: filled ? color : 'white', color: filled ? 'white' : color, border: `2px solid ${color}`,
  });

  return (
    <div>
      <div className="page-header" style={{ padding: '8px 0', marginBottom: 12 }}>
        <h1 style={{ color: '#2d3748', background: 'none', WebkitTextFillColor: '#2d3748' }}>
          {t('rewinders.title')}
        </h1>
        <button onClick={openCreate} className="btn" style={{ backgroundColor: '#667eea', color: '#fff', fontWeight: 600 }}>
          + {t('rewinders.add')}
        </button>
      </div>
      <p style={{ color: '#4a5568', fontSize: 14, marginTop: 0, marginBottom: 20 }}>{t('rewinders.help')}</p>

      {loading ? (
        <div className="loading-spinner"><div className="spinner" /></div>
      ) : (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 560 }}>
              <thead>
                <tr style={{ backgroundColor: '#667eea', color: 'white' }}>
                  <th style={{ ...headerCell, textAlign: 'left' }}>{t('rewinders.name')}</th>
                  <th style={{ ...headerCell, textAlign: 'center' }}>{t('rewinders.sortOrder')}</th>
                  <th style={{ ...headerCell, textAlign: 'center' }}>{t('rewinders.status')}</th>
                  <th style={{ ...headerCell, textAlign: 'center' }}>{t('rewinders.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {rewinders.length === 0 && (
                  <tr>
                    <td colSpan={4} style={{ padding: '48px 20px', textAlign: 'center', color: '#718096', fontStyle: 'italic' }}>
                      {t('rewinders.empty')}
                    </td>
                  </tr>
                )}
                {rewinders.map((r) => (
                  <tr key={r.id} style={{ borderBottom: '1px solid #e1e8ed', backgroundColor: r.is_active ? 'white' : '#f8f9fa' }}>
                    <td style={{ padding: '14px 12px', fontWeight: 600, color: '#2d3748' }}>{r.name}</td>
                    <td style={{ padding: '14px 12px', textAlign: 'center', color: '#4a5568' }}>{r.sort_order}</td>
                    <td style={{ padding: '14px 12px', textAlign: 'center' }}>
                      <span style={{
                        display: 'inline-block', padding: '4px 12px', borderRadius: 12, fontSize: 12, fontWeight: 600,
                        backgroundColor: r.is_active ? '#d1fae5' : '#fee2e2', color: r.is_active ? '#065f46' : '#991b1b',
                      }}>
                        {r.is_active ? t('rewinders.active') : t('rewinders.inactive')}
                      </span>
                    </td>
                    <td style={{ padding: '14px 12px' }}>
                      <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
                        <button className="btn" style={smallButton('#667eea', false)} onClick={() => openEdit(r)}>
                          {t('common.edit')}
                        </button>
                        <button className="btn" style={smallButton(r.is_active ? '#f59e0b' : '#10b981', true)} onClick={() => toggleActive(r)}>
                          {r.is_active ? t('rewinders.deactivate') : t('rewinders.activate')}
                        </button>
                        <button className="btn" style={smallButton('#ef4444', true)} onClick={() => handleDelete(r)}>
                          {t('common.delete')}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showModal && (
        <div style={{
          position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000,
        }}>
          <div style={{ backgroundColor: 'white', padding: 30, borderRadius: 12, maxWidth: 460, width: '90%' }}>
            <h2 style={{ marginTop: 0, color: '#667eea' }}>
              {editing ? t('rewinders.editTitle') : t('rewinders.addTitle')}
            </h2>
            <form onSubmit={handleSubmit}>
              <label style={{ display: 'block', marginBottom: 20, fontWeight: 500, color: '#4a5568' }}>
                {t('rewinders.name')} *
                <input
                  type="text"
                  className="input"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder={t('rewinders.namePlaceholder')}
                  required
                  maxLength={255}
                  style={{ marginTop: 8 }}
                />
              </label>
              <label style={{ display: 'block', marginBottom: 20, fontWeight: 500, color: '#4a5568' }}>
                {t('rewinders.sortOrder')}
                <input
                  type="number"
                  min={0}
                  className="input"
                  value={form.sort_order}
                  onChange={(e) => setForm({ ...form, sort_order: e.target.value })}
                  style={{ marginTop: 8, width: 120 }}
                />
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20, cursor: 'pointer', color: '#4a5568' }}>
                <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
                {t('rewinders.activeLabel')}
              </label>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button type="button" className="btn" style={{ backgroundColor: '#6c757d', color: 'white' }} onClick={() => setShowModal(false)}>
                  {t('common.cancel')}
                </button>
                <button type="submit" className="btn" disabled={saving} style={{ backgroundColor: '#667eea', color: 'white' }}>
                  {editing ? t('common.update') : t('common.create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Rewinders;
